<template>
  <div>
    <template v-if="!isOfficeLogin">
      <HomeworksMobile v-model="onboardingMobileActive" />
      <HomeworksDesktop v-model="onboardingDesktopActive" :isEmpty="sortedHomeworks.length === 0" />
      <HomeworksOnlyMobile v-model="onboardingOnlyMobileActive" />
      <HomeworksOnlyDesktop v-model="onboardingOnlyDesktopActive" :isEmpty="sortedHomeworks.length === 0" />
    </template>
    <div class="homeworks">
      <img class="homeworks__logo-mobile" src="@/assets/images/brand/logo.svg" />
      <img class="homeworks__logo-desktop" src="@/assets/images/brand/logo_full.svg" />
      <div class="homeworks__container">
        <div class="homeworks__header">
          <h3 class="homeworks__title">
            {{ $t("dashboard.task_list") }}
          </h3>
          <button v-if="(isFullAccess) && !isLoading" @click="startOnlyOnboarding" class="onboarding-button">
            <div class="flex items-center">
              <img id="classes-homework" src="@/assets/images/icons/i_pink.svg" />
              <p class="ml-2 text-pink-main text-lg font-medium">Zobacz jak to działa</p>
            </div>
          </button>
        </div>
        <div class="homeworks__tour">
          <Loader v-if="isLoading" />
          <HomeworkEmptyState :hasAccess="isFullAccess" v-if="!isLoading && !homeworks.length" />
          <TaskList v-else-if="homeworks && !isLoading" v-for="homework in sortedHomeworks" :key="homework.id"
            :homework="homework" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';
import * as Sentry from '@sentry/vue';

import TaskList from '@/components/TaskList/TaskList.vue';
import Loader from '@/components/Loader.vue';

import HomeworksMobile from '@/components/Popup/HomeworksMobile.vue';
import HomeworksDesktop from '@/components/Popup/HomeworksDesktop.vue';
import HomeworksOnlyMobile from '@/components/Popup/HomeworksOnlyMobile.vue';
import HomeworksOnlyDesktop from '@/components/Popup/HomeworksOnlyDesktop.vue';
import HomeworkEmptyState from '../../components/Homeworks/HomeworkEmptyState.vue';

export default {
  name: 'Homeworks',

  components: {
    TaskList,
    HomeworksMobile,
    HomeworksDesktop,
    HomeworksOnlyMobile,
    HomeworksOnlyDesktop,
    Loader,
    HomeworkEmptyState,
  },

  data() {
    return {
      windowWidth: 0,
      isLoading: false,
      modalOpen: false,
      onboardingMobileActive: false,
      onboardingDesktopActive: false,
      onboardingOnlyMobileActive: false,
      onboardingOnlyDesktopActive: false,
      tourTimeout: null,
    };
  },

  computed: {
    ...mapState('students', ['student']),
    ...mapState('homeworks', ['homeworks']),
    ...mapGetters('auth', ['isOfficeLogin', 'isFullAccess']),
    ...mapGetters('onboarding', [
      'getOnboardingProperty',
    ]),
    isMobileOnboardingDone() {
      return this.getOnboardingProperty({ deviceType: 'mobile', property: 'homework' });
    },
    isDesktopOnboardingDone() {
      return this.getOnboardingProperty({ deviceType: 'desktop', property: 'homework' });
    },

    sortedHomeworks() {
      return [...this.homeworks].sort((a, b) => {
        if (!a.lesson || !b.lesson) {
          return 0;
        }
        if (a.lesson.date > b.lesson.date) {
          return -1;
        }
        if (a.lesson.date === b.lesson.date) {
          return 0;
        }
        return 1;
      });
    },
  },

  methods: {
    ...mapActions('lessons', ['getLessons']),
    ...mapActions('students', ['updateStudent']),
    ...mapActions('lessons', ['clearLesson', 'clearLessons']),
    ...mapActions('homeworks', ['getHomeworks', 'clearHomeworks']),
    ...mapMutations('layout', ['showClassList', 'hideClassList']),
    ...mapMutations('layout', ['closeSidebar']),

    close() {
      this.closeSidebar();
    },

    getWindowWidth() {
      this.windowWidth = window.innerWidth;
      Event.$emit('cancelTour');
      this.onboardingMobileActive = false;
      this.onboardingDesktopActive = false;
      this.onboardingOnlyMobileActive = false;
      this.onboardingOnlyDesktopActive = false;
    },

    async getData(studentId) {
      const date = moment().format('YYYY-MM-DD');
      await this.getHomeworks({
        studentId,
      });
      await this.getLessons({
        studentId,
        payload: {
          dateFrom: date,
        },
      });
    },

    openModal() {
      this.modalOpen = !this.modalOpen;
    },

    startDesktopOnboarding() {
      if (this.student.access === 'full') {
        this.onboardingDesktopActive = !this.onboardingDesktopActive;
      }
    },

    startMobileOnboarding() {
      if (this.student.access === 'full') {
        this.onboardingMobileActive = !this.onboardingMobileActive;
      }
    },

    startOnlyOnboarding() {
      if (this.windowWidth < 992) {
        this.onboardingOnlyMobileActive = !this.onboardingOnlyMobileActive;
      } else {
        this.onboardingOnlyDesktopActive = !this.onboardingOnlyDesktopActive;
      }
    },

    checkIfTourCookieExist() {
      if (!this.student?.id || !this.student?.onboardingMobile || !this.student?.onboardingDesktop) return;

      if (this.student?.id) {
        this.tourTimeout = setTimeout(() => {
          const { group } = this.student || {};
          const { hasHomeworks } = group;
          const isMobile = this.windowWidth < 992;
          const studentId = this.student?.id;

          const profileMobileOnboarding = this.student?.onboardingMobile?.homework;
          const profileDesktopOnboarding = this.student?.onboardingDesktop?.homework;

          if (!studentId) return;

          if (isMobile) {
            if (hasHomeworks && !profileMobileOnboarding) {
              this.startMobileOnboarding();
              this.showClassList();
            }
          } else if (hasHomeworks && !profileDesktopOnboarding) {
            this.startDesktopOnboarding();
            this.showClassList();
          }
          this.showClassList();
        }, 500);
      }
    },

    logSentryHomeworksException({
      parentId, studentId, entrypoint, errorStatus, error,
    }) {
      Sentry.withScope((scope) => {
        scope.setUser({
          id: studentId,
        });
        scope.setTag('section', 'homeworks');
        scope.setTag('parent_id', parentId);
        scope.setTag('log_entrypoint', entrypoint);
        scope.setTag('error_status', errorStatus);
        scope.setLevel('info');
        Sentry.captureException(error);
      });
    },
  },

  mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.getWindowWidth);
  },

  async created() {
    if (!this.student || Object.keys(this.student).length === 0) {
      this.routerPush({ name: 'Auth.Welcome' });
    } else if (!this.isFullAccess) {
      this.isLoading = false;
      this.showClassList();
    } else {
      this.isLoading = true;
      this.hideClassList();
      this.clearLesson();
      this.clearLessons();
      this.clearHomeworks();

      if (this.student?.id) {
        const tempStudentId = this.student?.id;
        const tempParentId = this.parent?.id;
        try {
          await this.updateStudent(this.student.id);
        } catch (error) {
          this.logSentryHomeworksException({
            parentId: tempParentId,
            studentId: tempStudentId,
            entrypoint: 'student',
            errorStatus: error?.response?.status,
            error,
          });
          return;
        }
      }

      if (this.student === null || !this.student?.id
        || (this.$route.name === 'Main.Homeworks' && !this.student.group.hasHomeworks)) {
        this.routerPush({ name: 'Auth.Welcome' });
        return;
      }

      try {
        await this.getData(this.student.id);
      } catch (error) {
        this.logSentryHomeworksException({
          parentId: this.parent?.id,
          studentId: this.student?.id,
          entrypoint: 'data',
          errorStatus: error?.response?.status,
          error,
        });
        return;
      }

      this.isLoading = false;
      await this.checkIfTourCookieExist();
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getWindowWidth);
    clearTimeout(this.tourTimeout);
  },
};
</script>

<style lang="scss">
.homeworks {
  @apply min-h-full relative;
  z-index: 1;
  padding-top: 30px;

  @media screen and (min-width: 992px) {
    margin-left: 312px;
  }

  &__container {
    @apply mx-auto w-full pt-8 px-4;
    padding-bottom: 180px;

    @media screen and (min-width: 992px) {
      padding-left: 78px;
      padding-right: 40px;
    }
  }

  &__logo-mobile {
    @apply w-14 h-14 mx-auto block;

    @media screen and (min-width: 992px) {
      @apply hidden;
    }
  }

  &__logo-desktop {
    @apply mt-4 block;

    @media screen and (max-width: 991px) {
      @apply hidden;
    }

    @media screen and (min-width: 991px) {
      transform: translateX(78px);
    }
  }

  &__header {
    @apply flex flex-col items-start pb-8;

    @media screen and (min-width: 992px) {
      @apply flex-row items-center text-4xl;
    }

    .onboarding-button {
      @apply ml-0 mt-4;
      white-space: nowrap;

      @media screen and (min-width: 992px) {
        @apply mt-1 ml-4;
      }
    }
  }

  &__title {
    @apply text-2xl font-semibold;

    @media screen and (min-width: 992px) {
      @apply text-4xl;
    }
  }
}

#classes-homework {
  width: 21px;
}
</style>
